<template>
    <div class="seat-map-editor container-fluid">
        <Alert v-if="alert.show" type="success" :message="alert.message" />

        <div class="form-group">
            <label for="name">Nome</label>
            <input
                class="form-control"
                v-model="name"
                name="name"
                id="name"
                required
            />
        </div>

        <div v-if="isAreaMode">
            <div class="row">
                <div class="col-md-8">
                    <h2>Áreas</h2>
                </div>
                <div class="col-md-4">
                    <button class="btn btn-primary btn-block ml-auto" @click="handleNewArea">Nova área</button>
                </div>
            </div>
            <div class="row">
                <div class="col">
                    <ul class="list-group">
                        <li v-for="area in areas" :key="area.id" class="list-group-item d-flex justify-content-between align-items-center">
                            <span>{{area.name}}</span>
                            <div class="actions">
                                <span class="action-icon material-icons action" @click="selectArea(area)">edit</span>
                                <span class="action-icon material-icons action" @click="moveArea(area, -1)">move_up</span>
                                <span class="action-icon material-icons action" @click="moveArea(area, 1)">move_down</span>
                            </div>
                        </li>
                    </ul>
                </div>
            </div>
        </div>
        <div v-else-if="isSlotMode" class="row">
            <div class="col-8 canvas-area">
                <Canvas
                    :area="selectedArea"
                    :slots="selectedAreaSlots"
                    :onSlotSelection="handleSlotSelection"
                    :onSlotModification="handleSlotModification"
                    :selectedSlotId="selectedSlotId"
                ></Canvas>
            </div>
            <div class="col-4">
                <div>
                    <button class="btn action-icon material-icons" @click="unselectArea">arrow_back</button>
                    <strong>{{selectedArea.name}}</strong>
                </div>
                <div v-if="selectedSlot">
                    <p>
                        <strong>Tipo</strong>
                        <em>{{selectedSlot.type}}</em>
                    </p>
                    <p v-if="isResource(selectedSlot)">
                        <select @change="handleResourceSelection">
                            <option 
                                v-for="(opt, index) in resourceSelectOptions"
                                :value="opt.value"
                                :selected="opt.selected"
                                :key="`option-${opt.value}-${index}`"
                            >
                                {{ opt.label }}
                            </option>
                        </select>
                    </p>
                    <div v-if="isResource(selectedSlot)">
                        <h3>Recurso</h3>
                        <p>
                            <strong>Nome</strong>
                            <em>{{selectedSlot.resource.name}}</em>
                            <button class="btn action-icon material-icons" @click="handleSlotResourceValueChange(selectedSlot, 'name')">edit</button>
                        </p>
                        <p>
                            <strong>Código</strong>
                            <em>{{selectedSlot.resource.code}}</em>
                            <button class="btn action-icon material-icons" @click="handleSlotResourceValueChange(selectedSlot, 'code')">edit</button>
                        </p>
                        <p>
                            <strong>Formato</strong>
                            <select v-model="selectedSlot.shapeType" @change="onChangeFormat()">
                                <option value="rect">Retângulo</option>
                                <option value="circle">Círculo</option>
                            </select>
                        </p>
                        <p>
                            <strong>Capacidade</strong>
                            <em>{{selectedSlot.resource.capacity}}</em>
                            <button class="btn action-icon material-icons" @click="handleSlotResourceValueChange(selectedSlot, 'capacity', v => parseInt(v))">edit</button>
                        </p>
                        <p v-if="selectedResourceIsFresh">
                            <em class="text-success">Novo</em>
                        </p>
                        <p v-else-if="selectedResourceIsUsed">
                            <span class="text-primary">Utilizado em outros mapas de lugares</span>
                        </p>
                        <p>
                            <button class="btn btn-block btn-info" @click="duplicateSlot(selectedSlot)">Copiar Slot</button>
                        </p>
                        <p>
                            <button class="btn btn-block btn-danger" @click="removeSlot(selectedSlot)">Excluir</button>
                        </p>
                    </div>
                </div>
                <div v-else-if="bgUpload">
                    <div class="form-group">
                        <input type="file" class="form-control" @change="handleUploadSelection" accept="image/*" placeholder="Selecionar Imagem" />
                    </div>
                    <div class="form-group">
                        <input type="text" class="form-control" v-model="bgUpload.alt_text" placeholder="Texto alternativo" />
                    </div>
                    <div class="form-group">
                        <button type="button" class="btn btn-primary" @click="submitUploadImage">Salvar</button>
                        <button type="button" class="btn btn-outline" @click="cancelUploadImage">Cancelar</button>
                    </div>
                </div>
                <ul v-else>
                    <li>
                        <button class="btn btn-block" @click="renameArea(selectedArea)">Renomear</button>
                    </li>
                    <li>
                        <button class="btn btn-block" @click="handleUploadImage">Imagem de fundo</button>
                    </li>
                    <li>
                        <button class="btn btn-block" @click="handleNewSlot">Novo slot</button>
                    </li>
                    <li>
                        <button class="btn btn-block btn-danger" @click="removeArea(selectedArea)">Excluir</button>
                    </li>
                </ul>
            </div>
        </div>
        <div v-else class="row">
            <div class="col">
                <span>Carregando...</span>
            </div>
        </div>
        <div class="row">
            <div class="col">
                <hr />
                <button class="btn btn-block btn-primary" @click="handleSave">Salvar</button>
            </div>
        </div>
    </div>
</template>

<script>
import { v4 as uuidv4 } from 'uuid';
import Alert from '../Alert';
import Canvas from './Canvas';
import { requestJson } from '../../helpers';

const LOADING_MODE = 'loading';
const AREA_MODE = 'area';
const SLOT_MODE = 'slot';

const RESOURCE = 'resource';
const RECT = 'rect';
const CIRCLE = 'circle';

const sameArea = (a1, a2) => a1.id === a2.id;
const sameSlot = (s1, s2) => s1.id === s2.id;

const BASE_WIDTH = 600;
const BASE_HEIGHT = 400;

export default {
    components: { Alert, Canvas },
    props: {
        getSeatMapUrl: String,
        updateSeatMapUrl: String,
        getResourcesUrl: String,
        uploadImageUrl: String,
    },
    data(){
        return {
            mode: LOADING_MODE,
            persistedSeatMap: null,
            name: '',
            areas: [],
            slots: {},
            selectedAreaId: null,
            selectedSlotId: null,
            bgUpload: null,
            resourceOptions: [],
            alert: {
                show: false,
                type: 'success',
                message: '',
            }
        };
    },
    computed: {
        isAreaMode(){
            return this.mode === AREA_MODE;
        },
        isSlotMode(){
            return this.mode === SLOT_MODE;
        },
        selectedArea(){
            return this.areas.find(a => a.id === this.selectedAreaId) ?? null;
        },
        selectedAreaSlots(){
            return this.slots[this.selectedAreaId] ?? [];
        },
        selectedSlot(){
            return this.selectedArea && this.selectedSlotId ? this.selectedAreaSlots.find(s => s.id === this.selectedSlotId) : null;
        },
        selectedResource(){
            return this.selectedSlot && this.isResource(this.selectedSlot) ? this.selectedSlot.resource : null;
        },
        persistedSlotIds(){
            const ids = [];
            for (const key in this.slots) {
                const slots = this.slots[key];
                for (const slot of slots) {
                    if (this.isResource(slot) && slot.resource.id) {
                        ids.push(slot.resource.id);
                    }
                }
            }

            return ids;
        },
        resourceSelectOptions(){
            if (!this.selectedResource) {
                return null;
            }

            const options = [{
                value: 'new',
                label: 'Novo',
                selected: this.selectedSlot.resource.id == null,
            }];

            for (const res of this.resourceOptions) {
                if (!this.persistedSlotIds.includes(res.id) || res.id === this.selectedSlot.resource.id) {
                    options.push({
                        value: res.id,
                        label: res.name,
                        selected: this.selectedSlot.resource.id == res.id,
                    });
                }
            }

            return options;
        },
        selectedResourceIsFresh(){
            return this.selectedResource && this.selectedResource.id === null;
        },
        selectedResourceIsUsed(){
            return this.selectedResource && this.selectedResource.seatMapIds.filter(id => id != this.persistedSeatMap.id).length > 0;
        },
    },
    methods: {
        onChangeFormat(){
            let slot = this.selectedAreaSlots.find(s => sameSlot(s, this.selectedSlot));

            switch (slot.shapeType) {
                case RECT:
                    slot.rect = {
                        left: slot.circle ? slot.circle.left : 0,
                        top: slot.circle ? slot.circle.top : 0,
                        width: slot.circle ? slot.circle.radius * 2 : 0,
                        height: slot.circle ? slot.circle.radius * 2 : 0,
                        angle: 0,
                    }
                    slot.circle = null;
                    break;
                case CIRCLE:
                    slot.circle = {
                        left: slot.rect ? slot.rect.left : 0,
                        top: slot.rect ? slot.rect.top : 0,
                        radius: slot.rect ? slot.rect.width / 2 : 0,
                    }
                    slot.rect = null;
                default:
                    break;
            }

            this.$set(this.slots, this.selectedAreaId, this.selectedAreaSlots.map(s => sameSlot(s, slot) ? slot : s));

        },
        async pullSeatMap(){
            const seatMap = await requestJson(this.getSeatMapUrl);

            this.mountSeatMap(seatMap);
            this.mode = AREA_MODE;
        },
        mountSeatMap(seatMap){
            this.persistedSeatMap = seatMap;
            this.name = seatMap.name;
            this.areas = seatMap.data.areas;
            this.slots = this.areas.reduce((carry, area) => ({ ...carry, [area.id]: area.slots }), {});
        },

        handleNewArea(){
            const area = {
                id: uuidv4(),
                name: 'Nova área',
                width: BASE_WIDTH,
                height: BASE_HEIGHT,
                bgImage: null,
                slots: []
            };

            this.areas = [...this.areas, area];
        },
        selectArea(area){
            this.selectedAreaId = area.id;
            this.mode = SLOT_MODE;
        },
        renameArea(area){
            const name = prompt('Informe o novo nome da área');
            if (!name){
                return;
            }

            area = { ...area, name };
            this.areas = this.areas.map(a => sameArea(a, area) ? area : a);
        },
        moveArea(area, diff){
            const index = this.areas.findIndex(a => sameArea(a, area));
            const newIndex = index + diff;

            if (newIndex < 0 || newIndex >= this.areas.length) {
                return;
            }

            const newAreas = [ ...this.areas ];

            newAreas[index] = newAreas[newIndex];
            newAreas[newIndex] = area;

            this.areas = newAreas;
        },
        removeArea(area){
            if (!confirm('Tem certeza?')) {
                return;
            }

            if (this.selectedAreaId === area.id) {
                this.unselectArea();
            }

            this.$set(this.slots, area.id, null);

            const index = this.areas.findIndex(a => sameArea(a, area));
            this.areas = [...this.areas.slice(0, index), ...this.areas.slice(index + 1)];
        },
        unselectArea(){
            this.mode = AREA_MODE;
            this.selectedAreaId = null;
            this.selectedSlotId = null;
        },

        handleUploadImage(){
            this.bgUpload = {};
        },
        cancelUploadImage(){
            this.bgUpload = null;
        },
        handleUploadSelection(ev){
            this.bgUpload.image = ev.target.files[0] ?? null;
        },
        async submitUploadImage(){
            if (!this.bgUpload.image) {
                return;
            }

            const formData = new FormData();
            formData.set('image', this.bgUpload.image);
            formData.set('alt_text', this.bgUpload.alt_text || '');

            try {
                const img = await requestJson(this.uploadImageUrl, formData, 'POST');
                this.setBgImage(img);
            } finally {
                this.bgUpload = null;
            }
        },
        setBgImage(bgImage){
            const img = new Image();

            img.onload = () => {
                const width = BASE_WIDTH;
                const height = Math.round(img.height / img.width * width);

                const area = { ...this.selectedArea, bgImage, width, height };
                this.areas = this.areas.map(a => sameArea(a, area) ? area : a);
            };

            img.src = bgImage.styles.find(st => st.style === '_').url;
        },

        handleNewSlot(){
            this.createNewSlot();
        },
        createNewSlot(shapeType = 'rect', shape = null, capacity = 1){
            const defaultShape = {
                rect: {
                    left: 0,
                    top: 0,
                    width: 50,
                    height: 50,
                    angle: 0,
                },
                circle: {
                    left: 0,
                    top: 0,
                    radius: 25,
                },
            };
            
            const slot = {
                id: uuidv4(),
                type: RESOURCE,
                shapeType: shapeType,
                resourceId: null,
                resource: this.makeResource(capacity),
                [shapeType]: shape ?? defaultShape[shapeType],
            };

            this.$set(this.slots, this.selectedAreaId, [...this.selectedAreaSlots, slot]);

            return slot;
        },
        makeResource(capacity = 1){
            return {
                id: null,
                name: '',
                code: '',
                capacity,
                seatMapIds: [],
            };
        },
        handleResourceSelection(ev){
            const { value } = ev.target;

            if (this.selectedSlot) {
                const resource = value == 'new' ? this.makeResource() : this.resourceOptions.find(s => s.id === value);
                const newSlots = (this.slots[this.selectedAreaId] ?? []).map(slot => slot.id === this.selectedSlot.id ? ({
                    ...slot,
                    resource,
                    resourceId: resource.id,
                }) : slot);

                this.$set(this.slots, this.selectedAreaId, newSlots);
            }
        },
        isResource(slot){
            return slot.type === RESOURCE;
        },
        handleSlotSelection(slot){
            this.selectedSlotId = slot ? slot.id : null;

            if (slot) {
                this.refreshResourceOptions();
            }
        },
        async refreshResourceOptions(){
            const options = [];
            for (let page = 1, total = 1; page <= total; page++) {
                const { data, meta } = await requestJson(this.getResourcesUrl, { page });

                options.push(...data);
                total = meta.last_page;
            }

            this.resourceOptions = options;
        },
        handleSlotModification(currSlot, values){
            let slot = this.selectedAreaSlots.find(s => sameSlot(s, currSlot));

            slot[slot.shapeType] = values;

            this.$set(this.slots, this.selectedAreaId, this.selectedAreaSlots.map(s => sameSlot(s, slot) ? slot : s));
        },
        handleSlotResourceValueChange(currSlot, key, closure){
            let value = prompt('Insira o novo valor');
            if (value === null) {
                return;
            }

            if (closure) {
                value = closure(value);
            }

            let slot = this.selectedAreaSlots.find(s => sameSlot(s, currSlot));
            slot = { ...slot, resource: { ...slot.resource, [key]: value } };
            this.$set(this.slots, this.selectedAreaId, this.selectedAreaSlots.map(s => sameSlot(s, slot) ? slot : s));
        },
        duplicateSlot(slot){
            const diff = 25;
            const { width, height } = this.selectedArea;
            const left = slot[slot.shapeType].left + (Math.sign(width / 2 - slot[slot.shapeType].left) || 1) * diff;
            const top = slot[slot.shapeType].top + (Math.sign(height / 2 - slot[slot.shapeType].top) || 1) * diff;

            const shape = { ...slot[slot.shapeType], left, top };
            const newSlot = this.createNewSlot(slot.shapeType, shape, slot.resource.capacity);

            this.handleSlotSelection(newSlot);
        },
        removeSlot(slot){
            if (!confirm('Tem certeza?')) {
                return;
            }

            if (this.selectedSlotId === slot.id) {
                this.selectedSlotId = null;
            }

            const index = this.selectedAreaSlots.findIndex(s => sameSlot(s, slot));
            this.$set(this.slots, this.selectedAreaId, [...this.selectedAreaSlots.slice(0, index), ...this.selectedAreaSlots.slice(index + 1)]);
        },

        async handleSave(){
            const areas = this.areas.map(area => ({
                ...area,
                slots: this.slots[area.id] ?? []
            }));

            const data = { areas };

            const payload = {
                name: this.name,
                data
            }

            const seatMap = await requestJson(this.updateSeatMapUrl, payload, 'PATCH');
            if (!seatMap || !seatMap.id) {
                alert('Erro ao salvar!');
            } else {
                this.mountSeatMap(seatMap);

                this.showAlert('success', 'Mapa atualizado com sucesso!');
            }
        },
        showAlert(type, message){
            this.alert = {
                show: true,
                type: type,
                message: message,
            }
        },
    },
    mounted() {
        this.pullSeatMap();
    }
}
</script>

<style scoped lang="scss">
.seat-map-editor {
    max-width: 1000px;

    ul {
        padding: 0;
        list-style: none;
    }
}

.action {
    cursor: pointer;
}

@media(max-width: 1200px) {
    .canvas-area {
        max-width: 100% !important;
        flex: 0 0 100% !important;
    }
}
</style>
